import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import {
  DeliveryRequests,
  type DeliveryZone,
  type DeliveryZoneCoverage,
} from '~/api/DeliveryAddress';
import {
  DeliveryMechanic,
  DeliveryMethods,
  type ETACalculation,
  type ETADeliveryMechanicInfo,
  type ETADeliveryMethodInfo,
  type ETADeliveryProviderInfo,
  ETARequests,
  type GetETAResponse,
  toCalculation,
} from '~/api/ETA';
import { company } from '~/company/Company';
import { deliveryAddressStore } from '~/components/ModalDeliveryAddress/DeliveryAddressStore';
import {
  DOMAIN,
  REASON,
  reportError,
  reportWarning,
} from '~/services/telemetry';
import { checkoutStore, mainStore, userStore } from '~/stores';
import { storage as localStorage } from '~/stores/LocalStorage';
import { getDeliveryProvider } from '~/utils/deliveryProvider';
import {
  destinationKindForUsage,
  type ETADestinationKind,
} from '~/utils/destinationKind';

import type { BaseStore } from '../interfaces/BaseStore';
import { NoWarehouseError } from './NoWarehouseError';
import { getPrioritizedDeliveryMethod } from './utils';

/** Whether an ETA `pos` ("lat,lng") points at the given coordinates (~0.1 m). */
const isSamePos = (pos: string, { lat, lng }: GeoCoordinates) => {
  const [posLat, posLng] = pos.split(',').map(Number);
  return (
    Math.abs((posLat ?? Number.NaN) - lat) < 1e-6 &&
    Math.abs((posLng ?? Number.NaN) - lng) < 1e-6
  );
};

class DeliveryStore implements BaseStore {
  private readonly _requestPromiseHolder = new Map<
    string,
    Promise<ETACalculation>
  >();

  /** Points already reported as unserved, so the ETA poll cannot repeat them. */
  private readonly _reportedNoWarehouse = new Set<string>();

  deliveryProvider: Nullable<ETADeliveryProviderInfo> = null;
  deliveryMethod: Nullable<ETADeliveryMethodInfo> = null;
  deliveryMechanics: Nullable<ETADeliveryMechanicInfo> = null;
  deliveryProviderByMethod: Record<string, string> = {};

  constructor() {
    makeAutoObservable(this, {
      _requestPromiseHolder: false,
      _reportedNoWarehouse: false,
    } as any);
    makePersistable(this, {
      name: 'DeliveryStore',
      properties: ['deliveryProviderByMethod'],
      storage: localStorage,
    });
  }

  reset() {
    // biome-ignore lint/suspicious/noConsole: temporary JS-13586 trace
    console.warn('[JS-13586] DeliveryStore.reset', new Error().stack);
    runInAction(() => {
      this.deliveryProvider = null;
      this.deliveryMethod = null;
      this.deliveryMechanics = null;
      this.deliveryProviderByMethod = {};
    });
  }

  requestETA(
    pos: string,
    deliveryMethod?: DeliveryMethods,
    opts?: {
      receivePrioritizedDeliveryMethod?: boolean;
      signal?: AbortSignal;
      skipSideEffects?: boolean;
      destinationKind?: ETADestinationKind;
    },
  ): Promise<ETACalculation> {
    // skipSideEffects is part of the key on purpose: a probe result must never
    // satisfy a pending side-effectful request (or vice versa), even at the
    // cost of an occasional duplicate request for the same pos/method.
    const key = `${pos}-${deliveryMethod}-${opts?.skipSideEffects ?? false}`;

    // An entry only lives until its own request settles, but the request it
    // stands for can be cancelled long before that: the caller that opened it
    // aborts its controller and asks again for the same point. Dropping the
    // entry on abort keeps the next caller from joining a promise that is
    // already doomed to reject — which every caller reads as "no warehouse
    // serves this point" and shows as "we do not deliver here".
    if (!this._requestPromiseHolder.has(key)) {
      this._requestPromiseHolder.set(
        key,
        this._executeETARequest(pos, deliveryMethod, opts),
      );
      opts?.signal?.addEventListener('abort', () => this._clearPromise(key), {
        once: true,
      });
    }

    return this._requestPromiseHolder.get(key) as Promise<ETACalculation>;
  }

  getOrderDeliveryPayload(
    originDeliveryMethod: DeliveryMethods,
    mechanicOrigin?: DeliveryMechanic,
  ): {
    delivery_method: string;
    delivery_provider?: string;
    delivery_mechanic?: string;
  } {
    if (originDeliveryMethod === DeliveryMethods.CLICK_AND_COLLECT) {
      return {
        delivery_provider:
          this.deliveryProviderByMethod[DeliveryMethods.CLICK_AND_COLLECT] ||
          this.deliveryProvider?.code ||
          getDeliveryProvider(),
        delivery_method: this.deliveryMethod?.code || originDeliveryMethod,
        delivery_mechanic:
          this.deliveryMechanics?.PICKUP?.code || DeliveryMechanic.PICKUP,
      };
    }

    const mechanic =
      mechanicOrigin === DeliveryMechanic.SCHEDULED
        ? DeliveryMechanic.SCHEDULED
        : DeliveryMechanic.ON_DEMAND;

    const deliveryMethod =
      checkoutStore.deliveryMethod ||
      this.deliveryMethod?.code ||
      DeliveryMethods.COURIER_DELIVERY;

    return {
      delivery_provider:
        this.deliveryProviderByMethod[deliveryMethod] ||
        this.deliveryProvider?.code ||
        getDeliveryProvider(),
      delivery_method: deliveryMethod,
      delivery_mechanic: mechanic,
    };
  }

  async requestZones(): Promise<DeliveryZoneCoverage[]> {
    try {
      const {
        data: { coverages = [] },
      } = await DeliveryRequests.fetchDeliveryZones();

      return coverages.map((coverage) => ({
        warehouse: coverage.warehouse,
        zones: coverage.v2zones.map((v2Zone): DeliveryZone | undefined => {
          // TODO zh пока берем первого провайдера из ответа (пока у зоны один провайдер, будет по другому переделать)
          const provider = Object.values(v2Zone.deliveryProviders).at(0);

          const firstDeliveryMethod = Object.values(
            provider?.deliveryMethods ?? {},
          ).at(0);

          if (!firstDeliveryMethod) {
            return;
          }

          return {
            ...v2Zone,
            deliveryMethod: {
              shippingType: firstDeliveryMethod.code as DeliveryMethods,
            },
          };
        }),
      }));
    } catch (e: unknown) {
      reportError(DOMAIN.eta, e, {
        reason: REASON.eta.zonesFetchFailed,
        extras: {
          selectedZone: deliveryAddressStore.selectedZone
            ? toJS(deliveryAddressStore.selectedZone.zone)
            : undefined,
        },
      });
      return [];
    }
  }

  private async _executeETARequest(
    pos: string,
    deliveryMethod?: DeliveryMethods,
    opts?: {
      receivePrioritizedDeliveryMethod?: boolean;
      signal?: AbortSignal;
      skipSideEffects?: boolean;
      destinationKind?: ETADestinationKind;
    },
  ) {
    const {
      receivePrioritizedDeliveryMethod = false,
      signal,
      skipSideEffects = false,
      destinationKind,
    } = opts || {};
    const key = `${pos}-${deliveryMethod}-${skipSideEffects}`;

    let data: GetETAResponse;

    try {
      const resolvedDestinationKind = this._resolveDestinationKind(
        destinationKind,
        pos,
      );
      // biome-ignore lint/suspicious/noConsole: temporary JS-13586 trace
      console.warn('[JS-13586] ETA request', {
        pos,
        deliveryMethod,
        skipSideEffects,
        destinationKind: resolvedDestinationKind,
      });
      const response = await ETARequests.getETA({
        pos,
        signal,
        destinationKind: resolvedDestinationKind,
        ...(deliveryMethod ? { deliveryMethod } : {}),
      });
      data = response.data;
    } catch (error) {
      this._clearPromise(key);
      throw error;
    }

    if (
      !data.warehouse ||
      !Object.keys(data.warehouse.deliveryMethods ?? {}).length
    ) {
      if (!skipSideEffects) {
        this.reset();
        this._reportNoWarehouse(pos, deliveryMethod);
      }
      this._clearPromise(key);

      return Promise.reject(new NoWarehouseError(data));
    }

    if (!skipSideEffects) {
      this._handleV3Response(data, receivePrioritizedDeliveryMethod);
    }

    this._clearPromise(key);

    return toCalculation(
      data,
      deliveryMethod ?? checkoutStore.deliveryMethod,
      // Wrapped, not passed by reference: convertPenceToPounds reads `this`.
      (pence) => mainStore.convertPenceToPounds(pence),
    );
  }
  /**
   * Reports a point no warehouse serves, once per point and method.
   *
   * Here rather than in each caller: every entry into an address — the V3
   * list, the map modals, the address page, a deletion falling back to the
   * next address — asks this same question, and they handled the answer in
   * their own ways, several of them silently. One report at the source gives
   * every tenant the same visibility regardless of which flow they use.
   *
   * Deduped because the ETA is also polled: without it a single unserved
   * address would file an event every poll tick for the whole session.
   */
  private _reportNoWarehouse(pos: string, deliveryMethod?: DeliveryMethods) {
    const key = `${pos}-${deliveryMethod ?? 'any'}`;
    if (this._reportedNoWarehouse.has(key)) {
      return;
    }
    this._reportedNoWarehouse.add(key);

    reportWarning(DOMAIN.eta, 'No warehouse serves this point', {
      reason: REASON.eta.noWarehouse,
      extras: {
        pos,
        deliveryMethod: deliveryMethod ?? null,
        addressId: userStore.deliveryAddress?.addressId ?? null,
      },
    });
  }

  private _handleV3Response(
    data: GetETAResponse,
    receivePrioritizedDeliveryMethod: boolean,
  ) {
    runInAction(() => {
      this.deliveryProviderByMethod = {};

      const methods = data.warehouse?.deliveryMethods ?? {};

      for (const methodVal of Object.values(methods)) {
        const provider = methodVal.provider;
        if (provider) {
          // Keyed by the uppercased code, because that is what callers look up
          // with: `deliveryProviderByMethod[DeliveryMethods.COURIER_DELIVERY]`.
          this.deliveryProviderByMethod[methodVal.code.toUpperCase()] =
            provider.toUpperCase();
        }
      }
    });

    if (!receivePrioritizedDeliveryMethod) {
      const existingMethods = Object.values(data.warehouse.deliveryMethods).map(
        (method) => method.code.toUpperCase() as DeliveryMethods,
      );

      if (!existingMethods.includes(checkoutStore.deliveryMethod)) {
        const existing = existingMethods[0];

        if (existing) {
          checkoutStore.setDeliveryMethod(existing);
        }
      }

      return;
    }

    const prioritizedDeliveryMethod = getPrioritizedDeliveryMethod(
      data.warehouse.deliveryMethods,
    );

    if (prioritizedDeliveryMethod) {
      checkoutStore.setDeliveryMethod(prioritizedDeliveryMethod);
    }
  }

  /**
   * Fulfilment type to calculate the ETA for. Callers that know the choice
   * being made (the address modal, mid-switch) pass it explicitly; everywhere
   * else it follows the active delivery address. Only sent by tenants whose
   * PUDO and courier deliveries share one delivery method (the switcher
   * companies) — everywhere else the method itself disambiguates, and sending
   * `pup` to a tenant without pudo zones (e.g. Heimkaup) empties the ETA.
   * An active partner pickup point implies `partner_pup` only for its own
   * position: a request for any other point is a new address being picked.
   */
  private _resolveDestinationKind(
    explicit: ETADestinationKind | undefined,
    pos: string,
  ): ETADestinationKind | undefined {
    if (explicit === 'partner_pup') {
      return explicit;
    }

    const address = userStore.deliveryAddress;
    if (
      !explicit &&
      company.hasPartnerPickupPoints &&
      address?.partnerPupExternalId &&
      isSamePos(pos, address.coordinates)
    ) {
      return 'partner_pup';
    }

    if (!company.hasDeliveryTypeSwitcher) {
      return undefined;
    }

    return (
      explicit ?? destinationKindForUsage(userStore.deliveryAddress?.usage)
    );
  }

  private _clearPromise(key?: string) {
    if (!key) {
      this._requestPromiseHolder.clear();
      return;
    }

    if (this._requestPromiseHolder.has(key)) {
      this._requestPromiseHolder.delete(key);
    }
  }

  destroy(): void {
    // Clean up any resources or listeners here if necessary in future
    // Currently, no resources need to be explicitly released
  }
}

export default DeliveryStore;
